import React, { useState, useRef } from 'react';
import './FileUpload.css'; // Reuse existing styles
import apiClient from '../../services/apiClient';

const ImageUpload = ({ value, onChange, label = "Photo", placeholder = "Télécharger une image" }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image valide.');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('L\'image est trop volumineuse. Taille maximale: 5MB');
      return;
    }

    setUploading(true);
    
    try {
      // Check if we can use the backend API
      const isAuthenticated = await apiClient.verifyToken();
      
      if (isAuthenticated.valid) {
        console.log('📡 Uploading image to SQLite backend...');
        
        const response = await apiClient.uploadFiles([file]);
        console.log('📤 Upload response:', response);
        
        // Check if upload was successful first
        if (!response.success || !response.files || response.files.length === 0) {
          throw new Error(response.message || 'Upload failed - no files returned');
        }
        
        // Check for individual file errors
        const fileData = response.files[0];
        if (fileData.error) {
          throw new Error(fileData.error);
        }
        
        // Debug: Log the actual structure of files array
        console.log('📋 First file object structure:', fileData);
        console.log('📋 All file object keys:', Object.keys(fileData));
        
        // Handle different possible response formats
        let imageUrl = '';
        const fileObj = fileData;
        
        // Handle your backend's specific response format
        if (fileObj.data && fileObj.data.startsWith('data:image/')) {
          // Backend returns base64 data directly - use it!
          imageUrl = fileObj.data;
          console.log('✅ Using base64 data from backend');
        } else if (fileObj.public_url) {
          // Use public_url if available (already includes full domain)
          imageUrl = fileObj.public_url;
          console.log('✅ Using public_url from backend:', imageUrl);
        } else if (fileObj.file_url) {
          // Express.js backend returns file_url
          // If it starts with /media/ (not /api/media/), use it without API base
          if (fileObj.file_url.startsWith('/media/')) {
            imageUrl = fileObj.file_url.startsWith('http') ? fileObj.file_url : `https://dentoxcellence-backend.boufaamoundir.workers.dev${fileObj.file_url}`;
          } else {
            imageUrl = fileObj.file_url.startsWith('http') ? fileObj.file_url : `${apiClient.baseURL}${fileObj.file_url}`;
          }
          console.log('✅ Using file_url from backend:', imageUrl);
        } else if (fileObj.media_uuid || fileObj.uuid) {
          // Construct URL using media UUID
          const uuid = fileObj.media_uuid || fileObj.uuid;
          imageUrl = `${apiClient.baseURL}/media/${uuid}`;
          console.log('✅ Using media UUID:', imageUrl);
        } else if (fileObj.stored_filename) {
          imageUrl = `${apiClient.baseURL}/uploads/${fileObj.stored_filename}`;
        } else if (fileObj.filename) {
          imageUrl = `${apiClient.baseURL}/uploads/${fileObj.filename}`;
        } else if (fileObj.name) {
          imageUrl = `${apiClient.baseURL}/uploads/${fileObj.name}`;
        } else if (fileObj.file_name) {
          imageUrl = `${apiClient.baseURL}/uploads/${fileObj.file_name}`;
        } else if (fileObj.path) {
          imageUrl = fileObj.path.startsWith('http') ? fileObj.path : `${apiClient.baseURL}${fileObj.path}`;
        } else if (fileObj.url) {
          imageUrl = fileObj.url.startsWith('http') ? fileObj.url : `${apiClient.baseURL}${fileObj.url}`;
        } else if (fileObj.id) {
          imageUrl = `${apiClient.baseURL}/uploads/${fileObj.id}`;
        } else {
          // Log all available properties to debug
          console.log('🔍 Available fileObj properties:', Object.keys(fileObj));
          console.log('🔍 FileObj values:', fileObj);
          throw new Error('No valid filename found in upload response: ' + JSON.stringify(fileObj));
        }
        
        // Final validation - don't create fake fallback paths
        if (!imageUrl || imageUrl.includes('undefined') || imageUrl.includes('null')) {
          throw new Error('Invalid image URL generated from upload response');
        }
        
        onChange(imageUrl);
        console.log('✅ Image uploaded successfully:', imageUrl);
        console.log('📁 Final image URL:', imageUrl);
        
        // Show success message like FileUpload does
        // alert('✅ Image téléchargée avec succès!');
      } else {
        // Backend not available - show error instead of fake path
        throw new Error('Backend upload service not available. Please check your internet connection.');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      
      // Show more specific error message
      let errorMessage = 'Erreur lors du téléchargement. ';
      if (error.message && error.message.includes('Authentication required')) {
        errorMessage += 'Veuillez vous reconnecter.';
      } else if (error.message && error.message.includes('404')) {
        errorMessage += 'Endpoint non trouvé. Vérifiez votre backend.';
      } else if (error.message && error.message.includes('413')) {
        errorMessage += 'Fichier trop volumineux.';
      } else if (error.message && error.message.includes('400')) {
        errorMessage += 'Format de fichier non supporté.';
      } else {
        errorMessage += `Détails: ${error.message}`;
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  const handleManualPath = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="image-upload-container">
      {value ? (
        <div className="image-preview">
          <div className="preview-header">
            <h4>{label}</h4>
            <button 
              type="button"
              className="btn-remove"
              onClick={handleRemove}
              title="Supprimer l'image"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="preview-content">
            {(value.startsWith('http') || value.startsWith('/') || value.startsWith('data:image/')) ? (
              <img 
                src={value} 
                alt={label}
                className="image-preview-img"
                onError={(e) => {
                  console.error('❌ Image failed to load:', value);
                  console.error('❌ Image load error details:', e);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
                onLoad={() => {
                  console.log('✅ Image loaded successfully:', value);
                }}
              />
            ) : null}
            <div className="image-placeholder" style={{display: 'none'}}>
              <i className="fas fa-image"></i>
              <span>Aperçu non disponible</span>
            </div>
          </div>
          
          {/* Hidden: Image path not shown to client */}
        </div>
      ) : (
        <>
          <div
            className={`file-drop-zone ${isDragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Téléchargement en cours...</p>
                </>
              ) : (
                <>
                  <i className="fas fa-cloud-upload-alt"></i>
                  <p>{placeholder}</p>
                  <p className="drop-zone-hint">Glissez-déposez une image ou cliquez pour parcourir</p>
                  <p className="file-requirements">
                    <i className="fas fa-info-circle"></i>
                    Images acceptées: JPG, PNG, WebP (max 5MB)
                  </p>
                </>
              )}
            </div>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          {/* Hidden: Manual path input removed - client should not see worker URLs */}
        </>
      )}
    </div>
  );
};

export default ImageUpload;




